import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import Select from '@adsk/react-select';
import classNames from 'classnames';
import noop from 'lodash/noop';
import { objectType, arrayType } from '../../../../../../../../constants/proptype-validation/index';
import {
  assigneesForSelect,
  assigneeObjectToValue,
} from '../../../../../../../../utils/assigned-to-helpers';
import ReactSelectOption from './components/ReactSelectOption';

class AssignedToDropdown extends PureComponent {
  constructor() {
    super();
    this.state = {
      isOpen: false,
    };

    this.filterOptions = this.filterOptions.bind(this);
    this.filterOption = this.filterOption.bind(this);
    this.handleOpenClose = this.handleOpenClose.bind(this);
    this.handleSelect = this.handleSelect.bind(this);
    this.saveBeforeUnload = this.saveBeforeUnload.bind(this);
  }

  componentWillMount() {
    window.addEventListener('beforeunload', this.saveBeforeUnload);
  }

  saveBeforeUnload() {
    this.props.onBlur();
    window.removeEventListener('beforeunload', this.saveBeforeUnload);
  }

  UNSAFE_componentWillUnmount() {
    this.saveBeforeUnload();
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.select && this.state.isOpen && !prevState.isOpen) {
      this.select.input.focus();
      if (this.props.scrollToView) {
        this.wrapper.scrollIntoView();
      }
    }
  }

  _selectedValue() {
    const { currentValue, isMulti } = this.props;
    if (!currentValue) {
      return null;
    }
    const valueArray = (isMulti ? currentValue : [currentValue]).map(assigneeObjectToValue);
    return isMulti ? valueArray : valueArray[0];
  }

  _makeOptions() {
    const {
      oxygen: { users, companies, roles },
      specialOptions,
      isMulti,
    } = this.props;

    const assignees = assigneesForSelect(users, companies, roles);

    specialOptions.forEach((specialOpt) => {
      assignees.unshift({
        id: specialOpt.id,
        type: specialOpt.type,
        text: specialOpt.text,
        isSpecial: true,
      });
    });

    const options = [];

    assignees.map((option) => {
      if (option.isSpecial) {
        const specialOpt = {
          id: option.id,
          type: option.type,
          label: option.text,
          isSpecial: true,
          withCheckbox: isMulti,
        };
        specialOpt.value = assigneeObjectToValue(specialOpt);
        options.push(specialOpt);
      } else {
        options.push({
          disableTitle: option.text,
          disabled: true,
        });
        options.push(
          ...option.children.map((subOption) => {
            const opt = {
              id: subOption.id,
              label: subOption.text,
              type: option.type,
              typeLabel: option.text,
              withCheckbox: isMulti,
            };
            opt.value = assigneeObjectToValue(opt);
            if (subOption.title) {
              opt.description = subOption.title;
            }
            return opt;
          })
        );
      }
    });

    this._options = options;
    return options;
  }

  filterOption(option, filterValue) {
    if (!filterValue) {
      return true;
    }
    if (option.disableTitle) {
      return true;
    }
    filterValue = filterValue.toLowerCase();
    return ['label', 'description'].some(
      (filterKey) =>
        option[filterKey] && option[filterKey].toLowerCase().indexOf(filterValue) !== -1
    );
  }

  filterOptions(options, filterValue, excludeOptions, props) {
    let retOptions = options || [];

    const selectedOptions = [];
    const otherOptions = [];
    // eslint-disable-next-line consistent-return
    retOptions.filter((option) => {
      if (props.filterOption && !props.filterOption.call(this, option, filterValue)) {
        return false;
      }

      if (
        excludeOptions &&
        excludeOptions.some((selectedOpt) => selectedOpt.value === option.value)
      ) {
        option.isSelected = true;
        selectedOptions.push(option);
      } else {
        option.isSelected = false;
        otherOptions.push(option);
      }
    });
    excludeOptions && excludeOptions.splice(0, excludeOptions.length, ...selectedOptions); // change excludeOptions sort (by reference)
    retOptions = selectedOptions.concat(otherOptions);

    return retOptions;
  }

  handleOpenClose() {
    if (this.state.isOpen) {
      this.props.onBlur();
    }
    this.setState({ isOpen: !this.state.isOpen });
  }

  handleSelect(newValue) {
    const { isMulti } = this.props;
    if (isMulti) {
      const newValueArray = [];
      const options = this._options || this._makeOptions();
      newValue.forEach((val) => {
        const valIndex = newValueArray.indexOf(val);
        if (valIndex === -1) {
          newValueArray.push(val);
        } else {
          newValueArray.splice(valIndex, 1);
        }
      });
      newValue = options.filter(
        (opt) => newValueArray.findIndex((val) => opt.value === val.value) !== -1
      ); // sort new value array by options array
    }
    newValue = (isMulti ? newValue : [newValue]).map((v) => ({
      id: v.id,
      type: v.type,
    }));
    this.props.onSelect(isMulti ? newValue : newValue[0]);
  }

  render() {
    const { disabled, editable, textValue, placeholder, isEdit, isMulti, className } = this.props;
    if (!editable) {
      return <span className="isui-assignee__name isui-assignee__name--detail">{textValue}</span>;
    }

    const options = this._makeOptions();

    const selectClassName = classNames('react-select react-select__assign', {
      'react-select__edit': isEdit,
      'react-select__multi': isMulti,
      [className]: className,
    });

    return (
      <div
        ref={(wrapper) => {
          this.wrapper = wrapper;
        }}
      >
        <Select
          multi={isMulti}
          ref={(select) => {
            this.select = select;
          }}
          className={selectClassName}
          optionComponent={ReactSelectOption}
          placeholder={placeholder}
          value={this._selectedValue()}
          options={options}
          filterOptions={this.filterOptions}
          filterOption={this.filterOption}
          onOpen={this.handleOpenClose}
          onClose={this.handleOpenClose}
          onBlurResetInput={false}
          backspaceRemoves={false}
          searchable={this.state.isOpen}
          disabled={disabled}
          clearable={false}
          onChange={this.handleSelect}
          onFocus={this.props.onFocus}
        />
      </div>
    );
  }
}

AssignedToDropdown.displayName = 'AssignedToDropdown';

AssignedToDropdown.defaultProps = {
  specialOptions: [],
  onSelect: noop,
  onFocus: noop,
  onBlur: noop,
};

AssignedToDropdown.propTypes = {
  disabled: PropTypes.bool,
  editable: PropTypes.bool,
  isEdit: PropTypes.bool,
  scrollToView: PropTypes.bool,
  isMulti: PropTypes.bool,
  textValue: PropTypes.string,
  placeholder: PropTypes.string,
  specialOptions: arrayType,
  currentValue: PropTypes.oneOfType([objectType, arrayType]),
  oxygen: objectType.isRequired,
  className: PropTypes.string,
  onSelect: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
};

export default AssignedToDropdown;
